<template>
  <div class="visual-canvas">
    <!-- 빈 상태 -->
    <div v-if="!page || !page.steps || page.steps.length === 0" class="empty-canvas">
      <a-empty
        description="페이지에 스텝이 없습니다"
        image="simple"
      >
        <template #extra>
          <a-button type="dashed" @click="addNewStep">
            <plus-outlined /> 스텝 추가하기
          </a-button>
        </template>
      </a-empty>
    </div>

    <!-- 스텝 목록 -->
    <div v-else class="steps-wrapper">
      <div
        v-for="(step, stepIndex) in page.steps"
        :key="step.stepId"
        class="step-container"
        :class="{ selected: selectedItem?.stepId === step.stepId }"
        @click="$emit('select', step)"
      >
        <!-- 스텝 헤더 -->
        <div class="step-header">
          <div class="step-info">
            <span class="step-number">Step {{ stepIndex + 1 }}</span>
            <a-typography-text
              v-if="editingName !== step.stepId"
              class="step-name"
              @click.stop="startEditName(step)"
            >
              {{ step.stepName && step.stepName.trim() ? step.stepName : 'Unnamed Step' }}
            </a-typography-text>
            <a-input
              v-else
              v-model:value="tempName"
              size="small"
              @blur="saveEditName(step)"
              @keyup.enter="saveEditName(step)"
              @keyup.esc="cancelEditName"
              @click.stop
              style="width: 200px;"
            />
            <a-badge :color="getStepTypeColor(step.stepType)" :text="step.stepType" />
          </div>
          <div class="step-actions">
            <a-tooltip title="스텝 설정">
              <a-button size="small" type="text" @click.stop="editStep(step)">
                <setting-outlined />
              </a-button>
            </a-tooltip>
            <a-popconfirm
              title="이 스텝을 삭제하시겠습니까?"
              ok-text="삭제"
              cancel-text="취소"
              placement="bottomRight"
              @confirm="removeStep(step, stepIndex)"
            >
              <a-tooltip title="스텝 삭제">
                <a-button size="small" type="text" danger @click.stop>
                  <delete-outlined />
                </a-button>
              </a-tooltip>
            </a-popconfirm>
          </div>
        </div>

        <!-- 섹션 목록 -->
        <div class="sections-wrapper">
          <!-- 섹션 추가 버튼 -->
          <a-button
            type="dashed"
            block
            style="margin-bottom: 12px;"
            @click.stop="$emit('add-section', step)"
          >
            <plus-outlined /> 섹션 추가
          </a-button>

          <div
            v-for="(section, sectionIndex) in step.sections"
            :key="section.sectionId"
            class="section-wrapper"
          >
            <a-card
              class="section-card"
              :class="{
                selected: selectedItem?.sectionId === section.sectionId,
                [`section-${section.sectionType}`]: true
              }"
              :body-style="{ padding: '12px' }"
              @click.stop="$emit('select', section)"
            >
              <!-- 섹션 헤더 -->
              <div class="section-header">
                <div class="section-info">
                  <component :is="getSectionIcon(section.sectionType)" class="section-icon" />
                  <span class="section-name">{{ section.sectionName || getSectionTypeLabel(section.sectionType) }}</span>
                  <a-tag :color="getSectionTypeColor(section.sectionType)" style="margin-left: 8px;">
                    {{ getSectionTypeLabel(section.sectionType) }}
                  </a-tag>
                </div>
                <div class="section-actions">
                  <a-tooltip title="툴팁 추가">
                    <a-button size="small" type="dashed" @click.stop="addTooltipToSection(section)">
                      <question-circle-outlined />
                    </a-button>
                  </a-tooltip>
                  <a-button size="small" type="text" @click.stop="editSection(section)">
                    <edit-outlined />
                  </a-button>
                  <a-popconfirm
                    title="삭제하시겠습니까?"
                    ok-text="삭제"
                    cancel-text="취소"
                    placement="bottomRight"
                    @confirm="$emit('remove', section, step)"
                  >
                    <a-button size="small" type="text" danger @click.stop>
                      <delete-outlined />
                    </a-button>
                  </a-popconfirm>
                </div>
              </div>

              <!-- 섹션 콘텐츠 -->
              <div class="section-content">
                <!-- 컴포넌트 목록 -->
                <div class="components-list">
                  <div
                    v-for="(comp, compIndex) in section.components"
                    :key="comp.id"
                    class="component-item"
                  >
                    <div class="component-wrapper">
                      <!-- 순서 이동 버튼들 -->
                      <div class="component-order-buttons">
                        <a-button
                          size="small"
                          type="text"
                          :disabled="compIndex === section.components.length - 1"
                          @click.stop="moveComponent(section, compIndex, 1)"
                          title="뒤로 이동"
                        >
                          <arrow-right-outlined />
                        </a-button>
                        <a-button
                          size="small"
                          type="text"
                          :disabled="compIndex === 0"
                          @click.stop="moveComponent(section, compIndex, -1)"
                          title="앞으로 이동"
                        >
                          <arrow-left-outlined />
                        </a-button>
                      </div>

                      <a-tag
                        closable
                        @close="removeComponent(section, compIndex)"
                        @click.stop="editComponent(comp)"
                        style="cursor: pointer; flex: 1;"
                      >
                        <component :is="getComponentIcon(comp.type)" />
                        {{ getComponentLabel(comp) }}
                        <edit-outlined style="margin-left: 4px; font-size: 10px;" />
                      </a-tag>
                    </div>
                  </div>

                  <!-- 컴포넌트 추가 버튼들 -->
                  <a-button
                    size="small"
                    type="dashed"
                    @click.stop="addComponent(section, 'text')"
                    style="margin-right: 8px;"
                  >
                    <plus-outlined /> 텍스트 추가
                  </a-button>
                  <a-button
                    size="small"
                    type="dashed"
                    @click.stop="addComponent(section, 'button')"
                    style="margin-right: 8px;"
                  >
                    <plus-outlined /> 버튼 추가
                  </a-button>
                  <a-button
                    size="small"
                    type="dashed"
                    @click.stop="addComponent(section, 'image')"
                    style="margin-right: 8px;"
                  >
                    <plus-outlined /> 이미지 추가
                  </a-button>
                  <a-button
                    size="small"
                    type="dashed"
                    @click.stop="addComponent(section, 'backgroundImage')"
                    style="margin-right: 8px;"
                  >
                    <plus-outlined /> 배경 이미지 추가
                  </a-button>
                  <a-button
                    size="small"
                    type="dashed"
                    @click.stop="addComponent(section, 'survey')"
                    style="margin-right: 8px;"
                  >
                    <plus-outlined /> 설문문항 추가
                  </a-button>
                  <a-button
                    size="small"
                    type="dashed"
                    @click.stop="addComponent(section, 'tooltip')"
                  >
                    <plus-outlined /> 툴팁 추가
                  </a-button>
                </div>

                <!-- 비어있을 때 -->
                <div v-if="!section.components || section.components.length === 0" class="empty-hint">
                  <span style="color: #999; font-size: 12px;">버튼 또는 이미지를 추가하세요</span>
                </div>
              </div>
            </a-card>
          </div>
        </div>
      </div>

      <!-- 스텝 추가 버튼 -->
      <div class="add-step-button">
        <a-button type="dashed" block @click="addNewStep">
          <plus-outlined /> 새 스텝 추가
        </a-button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
  ThunderboltOutlined,
  PictureOutlined,
  AlignLeftOutlined,
  BlockOutlined,
  ContainerOutlined,
  QuestionCircleOutlined,
  FormOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  ProfileOutlined
} from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';

export default {
  name: 'VisualCanvas',
  components: {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    SettingOutlined,
    ThunderboltOutlined,
    PictureOutlined,
    AlignLeftOutlined,
    BlockOutlined,
    ContainerOutlined,
    QuestionCircleOutlined,
    FormOutlined,
    ProfileOutlined,
    ArrowLeftOutlined,
    ArrowRightOutlined
  },
  props: {
    page: Object,
    selectedItem: Object
  },
  emits: ['select', 'update', 'remove', 'add-section'],
  setup(props, { emit }) {
    const editingName = ref(null);
    const tempName = ref('');

    // 이름 편집
    const startEditName = (item) => {
      editingName.value = item.stepId || item.sectionId;
      tempName.value = item.stepName || item.sectionName || '';
    };

    const saveEditName = (item) => {
      if (item.stepId) {
        // 빈 문자열이거나 공백만 있으면 기본값 설정
        item.stepName = tempName.value.trim() || 'Unnamed Step';
      } else {
        item.sectionName = tempName.value.trim() || 'Unnamed Section';
      }
      editingName.value = null;
      tempName.value = '';
    };

    const cancelEditName = () => {
      editingName.value = null;
      tempName.value = '';
    };

    // 스텝 타입 색상
    const getStepTypeColor = (type) => {
      const colors = {
        intro: 'green',
        main: 'blue',
        quiz: 'cyan',
        form: 'orange',
        result: 'purple',
        outro: 'gray'
      };
      return colors[type] || 'default';
    };

    // 섹션 아이콘
    const getSectionIcon = (type) => {
      const icons = {
        header: 'block-outlined',
        hero: 'picture-outlined',
        content: 'container-outlined',
        quiz: 'question-circle-outlined',
        form: 'form-outlined',
        custom: 'profile-outlined'
      };
      return icons[type] || 'block-outlined';
    };

    // 섹션 타입 라벨
    const getSectionTypeLabel = (type) => {
      const labels = {
        header: '헤더',
        hero: '히어로',
        content: '콘텐츠',
        quiz: '퀴즈',
        form: '폼',
        custom: '커스텀'
      };
      return labels[type] || type;
    };

    // 섹션 타입 색상
    const getSectionTypeColor = (type) => {
      const colors = {
        header: 'purple',
        hero: 'cyan',
        content: 'orange',
        quiz: 'blue',
        form: 'magenta',
        custom: 'default'
      };
      return colors[type] || 'default';
    };

    // 스텝 편집
    const editStep = (step) => {
      emit('update', step, { editing: true });
    };

    // 스텝 삭제
    const removeStep = (step, index) => {
      props.page.steps.splice(index, 1);
      message.success('스텝이 삭제되었습니다');
    };

    // 섹션 편집
    const editSection = (section) => {
      emit('update', section, { editing: true });
    };

    // 컴포넌트 편집
    const editComponent = (component) => {
      emit('update', component, { editing: true });
    };

    // 컴포넌트 아이콘 가져오기
    const getComponentIcon = (type) => {
      const icons = {
        text: 'font-size-outlined',
        button: 'thunderbolt-outlined',
        image: 'picture-outlined',
        backgroundImage: 'bg-colors-outlined',
        survey: 'form-outlined',
        tooltip: 'question-circle-outlined'
      };
      return icons[type] || 'block-outlined';
    };

    // 컴포넌트 라벨 가져오기
    const getComponentLabel = (comp) => {
      if (comp.type === 'text') {
        const text = comp.content?.txt || '텍스트';
        return text.length > 20 ? text.substring(0, 20) + '...' : text;
      } else if (comp.type === 'button') {
        return comp.content?.txt || '버튼';
      } else if (comp.type === 'image') {
        return comp.content?.alt || '이미지';
      } else if (comp.type === 'backgroundImage') {
        return '배경 이미지';
      } else if (comp.type === 'survey') {
        const text = comp.content?.txt || '설문문항';
        return text.length > 20 ? text.substring(0, 20) + '...' : text;
      } else if (comp.type === 'tooltip') {
        const text = comp.content?.text || '툴팁';
        return text.length > 20 ? text.substring(0, 20) + '...' : text;
      }
      return comp.type;
    };

    // 컴포넌트 추가
    const addComponent = (section, type) => {
      if (!section.components) section.components = [];

      let content;
      let successMessage;

      switch(type) {
        case 'text':
          content = { txt: '텍스트 내용을 입력하세요' };
          successMessage = '텍스트가 추가되었습니다';
          break;
        case 'button':
          content = { txt: '버튼 텍스트' };
          successMessage = '버튼이 추가되었습니다';
          break;
        case 'image':
          content = { imageUrl: '', alt: '이미지' };
          successMessage = '이미지가 추가되었습니다';
          break;
        case 'backgroundImage':
          content = { imageUrl: '' };
          successMessage = '배경 이미지가 추가되었습니다';
          break;
        case 'survey':
          content = { txt: '설문 텍스트를 입력하세요' };
          successMessage = '설문문항이 추가되었습니다';
          break;
        case 'tooltip':
          content = { text: '툴팁 내용을 입력하세요', position: 'top' };
          successMessage = '툴팁이 추가되었습니다';
          break;
        default:
          content = {};
          successMessage = '컴포넌트가 추가되었습니다';
      }

      const newComponent = {
        id: `${type}-${Date.now()}`,
        type: type,
        content: content,
        order: section.components.length + 1,
        isActive: true
      };

      section.components.push(newComponent);
      message.success(successMessage);
    };

    // 컴포넌트 삭제
    const removeComponent = (section, index) => {
      section.components.splice(index, 1);
    };

    // 컴포넌트 순서 이동
    const moveComponent = (section, index, direction) => {
      const newIndex = index + direction;

      // 배열 범위를 벗어나지 않도록 체크
      if (newIndex < 0 || newIndex >= section.components.length) {
        return;
      }

      // 컴포넌트 순서 변경
      const [movedItem] = section.components.splice(index, 1);
      section.components.splice(newIndex, 0, movedItem);

      // order 값 재정렬
      section.components.forEach((comp, idx) => {
        comp.order = idx + 1;
      });

      message.success('컴포넌트 순서가 변경되었습니다');
    };

    // 새 스텝 추가
    const addNewStep = () => {
      if (!props.page.steps) {
        props.page.steps = [];
      }
      const newStep = {
        stepId: `step-${Date.now()}`,
        stepName: `새 스텝 ${props.page.steps.length + 1}`,
        stepType: 'main',
        order: props.page.steps.length + 1,
        sections: []
      };
      props.page.steps.push(newStep);
      message.success('새 스텝이 추가되었습니다');
    };

    // 섹션에 툴팁 추가
    const addTooltipToSection = (section) => {
      if (!section.metadata) {
        section.metadata = {};
      }

      // metadata에 tooltip 필드가 없으면 기본값 추가
      if (!section.metadata.tooltip) {
        section.metadata.tooltip = {
          text: '툴팁 내용을 입력하세요',
          position: 'top'
        };
        message.success('툴팁이 추가되었습니다. 섹션 설정에서 내용을 편집할 수 있습니다.');
      } else {
        message.info('이미 툴팁이 추가되어 있습니다. 섹션 설정에서 편집할 수 있습니다.');
      }

      // 섹션 설정 편집 모드로 전환
      emit('update', section, { editing: true });
    };

    return {
      editingName,
      tempName,
      // Methods
      startEditName,
      saveEditName,
      cancelEditName,
      getStepTypeColor,
      getSectionIcon,
      getSectionTypeLabel,
      getSectionTypeColor,
      editStep,
      removeStep,
      editSection,
      editComponent,
      getComponentIcon,
      getComponentLabel,
      addComponent,
      removeComponent,
      moveComponent,
      addNewStep,
      addTooltipToSection
    };
  }
};
</script>

<style lang="scss" scoped>
.visual-canvas {
  min-height: 500px;
  position: relative;

  .empty-canvas {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    border: 2px dashed #d9d9d9;
    border-radius: 8px;
    background: white;
  }

  .steps-wrapper {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .step-container {
    background: white;
    border-radius: 8px;
    padding: 16px;
    border: 2px solid transparent;
    transition: all 0.3s;

    &:hover {
      border-color: #e6f7ff;
    }

    &.selected {
      border-color: #1890ff;
      background: #f6ffed;
    }

    .step-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
      padding-bottom: 12px;
      border-bottom: 1px solid #f0f0f0;

      .step-info {
        display: flex;
        align-items: center;
        gap: 12px;

        .step-number {
          font-weight: 600;
          color: #1890ff;
        }

        .step-name {
          font-size: 16px;
          font-weight: 500;
          cursor: text;

          &:hover {
            background: #f0f0f0;
            padding: 2px 8px;
            border-radius: 4px;
          }
        }
      }

      .step-actions {
        display: flex;
        gap: 4px;
      }
    }
  }

  .sections-wrapper {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .section-card {
    cursor: pointer;
    transition: all 0.3s;
    border: 2px solid transparent;

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      transform: translateY(-2px);
    }

    &.selected {
      border-color: #1890ff;
    }


    &.section-header {
      border-left: 4px solid #722ed1;
    }

    &.section-content {
      border-left: 4px solid #fa8c16;
    }

    &.section-quiz {
      border-left: 4px solid #13c2c2;
    }

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;

      .section-info {
        display: flex;
        align-items: center;
        gap: 8px;

        .section-icon {
          color: #1890ff;
        }

        .section-name {
          font-weight: 500;
        }
      }

      .section-actions {
        display: flex;
        gap: 4px;
        opacity: 0;
        transition: opacity 0.3s;
      }

      &:hover .section-actions {
        opacity: 1;
      }
    }

    .section-content {
      .components-list {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        padding: 8px;
        align-items: center;
      }

      .component-item {
        .component-wrapper {
          display: flex;
          align-items: center;
          gap: 4px;

          .component-order-buttons {
            display: flex;
            flex-direction: column;
            gap: 0;

            .ant-btn {
              padding: 0 4px;
              height: 20px;
              line-height: 20px;

              &:disabled {
                opacity: 0.3;
              }
            }
          }
        }

        .ant-tag {
          padding: 4px 12px;
          display: flex;
          align-items: center;
          gap: 6px;

          &:hover {
            border-color: #1890ff;
          }
        }
      }

      .empty-hint {
        padding: 8px;
        text-align: center;
        width: 100%;
      }
    }
  }

  .add-step-button {
    margin-top: 16px;
  }
}

</style>

<style lang="scss">
// Popconfirm 버튼 레이아웃 수정 (전역 적용)
.ant-popconfirm {
  .ant-popconfirm-buttons {
    display: flex !important;
    flex-direction: row !important;
    justify-content: flex-end !important;
    gap: 8px !important;

    button {
      margin-left: 0 !important;
      margin-right: 0 !important;

      &:first-child {
        margin-right: 8px !important;
      }
    }
  }
}

.ant-popover {
  .ant-popconfirm-buttons {
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;

    > .ant-btn {
      margin: 0 4px !important;

      &:first-child {
        margin-left: 0 !important;
      }

      &:last-child {
        margin-right: 0 !important;
      }
    }
  }
}
</style>